import { UserAvatar } from "@/components/ui/stacked-avatars";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { Scribbles } from "@somethings-ui/core/assets";
import type {
  HotTakeAnswer,
  HotTakesConfigStatement,
} from "@somethings-ui/core/hot-takes";

interface HotTakesStatementCardProps {
  statement: HotTakesConfigStatement;
  teenAnswer: HotTakeAnswer | null;
  mentorName: string;
  teenName: string;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
}

// Full-size single-statement card used in the Hub viewer carousel.
// Mirrors the teen mobile SummaryCard layout — big centered take + two
// answer pills at the bottom with avatars floating above each pill.
export function HotTakesStatementCard({
  statement,
  teenAnswer,
  mentorName,
  teenName,
  mentorAvatarUrl,
  teenAvatarUrl,
}: HotTakesStatementCardProps) {
  const mentor = useSignedMedia(mentorAvatarUrl ?? undefined);
  const teen = useSignedMedia(teenAvatarUrl ?? undefined);

  const disagreeAvatars: Avatar[] = [
    ...(statement.mentorAnswer === "disagree"
      ? [{ key: "mentor", name: mentorName, url: mentor.url }]
      : []),
    ...(teenAnswer === "disagree"
      ? [{ key: "teen", name: teenName, url: teen.url }]
      : []),
  ];
  const agreeAvatars: Avatar[] = [
    ...(statement.mentorAnswer === "agree"
      ? [{ key: "mentor", name: mentorName, url: mentor.url }]
      : []),
    ...(teenAnswer === "agree"
      ? [{ key: "teen", name: teenName, url: teen.url }]
      : []),
  ];

  return (
    <div
      className="relative h-[538px] w-[361px] overflow-hidden rounded-[24px] shadow-[0_0_16px_rgba(0,0,0,0.08)]"
      style={{
        backgroundImage: "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
      }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="absolute inset-0 flex items-center justify-center px-6">
        <p
          className="break-words text-center font-bold tracking-tight text-white"
          style={{
            fontSize: 36,
            lineHeight: 1.1,
            letterSpacing: -0.9,
            maxWidth: 297,
          }}
        >
          {statement.text}
        </p>
      </div>

      <div
        className="absolute left-0 right-0 flex items-end justify-center"
        style={{ bottom: 48, gap: 16 }}
      >
        <PillWithAvatars label="disagree" side="left" avatars={disagreeAvatars} />
        <PillWithAvatars label="agree" side="right" avatars={agreeAvatars} />
      </div>
    </div>
  );
}

interface Avatar {
  key: string;
  name: string;
  url?: string;
}

function PillWithAvatars({
  label,
  side,
  avatars,
}: {
  label: string;
  side: "left" | "right";
  avatars: Avatar[];
}) {
  const tilt = side === "left" ? -10.82 : 14.77;

  return (
    <div className="flex flex-col items-center" style={{ height: 96 }}>
      <div
        className="flex items-center"
        style={{ height: 54, marginBottom: -6 }}
      >
        {avatars.map((avatar, i) => (
          <div
            key={`${avatar.key}-${i}`}
            style={{
              transform: `rotate(${tilt}deg)`,
              marginLeft: i > 0 ? -12 : 0,
              zIndex: avatars.length - i,
            }}
          >
            <UserAvatar
              imageUrl={avatar.url}
              name={avatar.name}
              size={46}
              showBorder
            />
          </div>
        ))}
      </div>
      <div
        className="flex items-center rounded-2xl border border-white"
        style={{ paddingInline: 16, paddingBlock: 8 }}
      >
        <span
          className="font-bold tracking-tight text-white"
          style={{ fontSize: 20, lineHeight: 1.15, letterSpacing: -0.2 }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
